/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header/index';
import Card from 'components/inputs/Card';
import { Row, Col } from 'react-bootstrap';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { BsUpload } from 'react-icons/bs';
import { FiDownload } from 'react-icons/fi';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE } from 'components/common/constant';
import {
  CREATE_PRELOADED_CAMPAIGN,
  UPDATE_PRELOADED_CAMPAIGN,
  GET_PRELOADED_CAMPAIGN,
} from 'actions/preloadedCampaign';
import { FETCH_USER_GROUP } from 'actions/userGroup';

const toDateInput = (d) => (d ? moment(d).format('YYYY-MM-DD') : '');

// Policy rules — fixed per client sign-off (Q1-Q7). Not admin-configurable; sent
// with every campaign so behaviour is explicit and consistent.
const FIXED_POLICY = {
  percentage_base: 'gross', // Q2 — M% on gross bill incl GST
  rounding: 'floor', // Q2 — rounded down to nearest ₹1
  decrement_n_on: 'applied', // Q1 — N counts only when credit applied
  stack_with_offers: false, // Q3 — never with session-level offers
  stack_with_chargecoin: true, // Q3 — always allowed with ChargeCoin
  exclude_discounted_stations: false, // Q3 — discounted stations allowed
  exclude_tata_ev: true, // Q6 — Tata.ev excluded
  include_owned_stations: true, // Q5 — ChargeZone-owned stations always included
  expire_leftover_on_n: true, // Q4 — reverse leftover immediately on N
  restore_on_refund: true, // Q7 — restore credit on full refund
  refund_count_session_min_utilised: 5, // Q7 — ₹5 partial-refund threshold
};

const PreloadedCampaignForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { campaignId } = useParams();
  const { navigateTo } = useContext(NavContext);
  const isEdit = !!campaignId;

  const campaignDetail = useSelector((state) => state.preloadedCampaign.preloadedCampaignDetail);
  const allUserGroup = useSelector((state) => state.userGroup.userGroups);
  const userGroupPage = useSelector((state) => state.userGroup.page);
  const userGroupTotalPage = useSelector((state) => state.userGroup.totalPages);

  const getAllUserGroup = useCallback((data = {}) => {
    dispatch({ type: FETCH_USER_GROUP.REQUEST, payload: data });
  }, []);

  useEffect(() => {
    getAllUserGroup();
    if (isEdit) {
      dispatch({ type: GET_PRELOADED_CAMPAIGN.REQUEST, payload: campaignId });
    }
  }, [campaignId]);

  const initialValues = isEdit
    ? {
        name: _.get(campaignDetail, 'name', ''),
        campaign_code: _.get(campaignDetail, 'campaign_code', ''),
        amount: _.get(campaignDetail, 'amount', ''),
        max_sessions: _.get(campaignDetail, 'max_sessions', ''),
        percentage_per_session: _.get(campaignDetail, 'percentage_per_session', ''),
        min_units: _.get(campaignDetail, 'min_units', 0),
        start_date: toDateInput(_.get(campaignDetail, 'start_date')),
        end_date: toDateInput(_.get(campaignDetail, 'end_date')),
        cohort_group: _.get(campaignDetail, 'cohort_group.id') || _.get(campaignDetail, 'cohort_group', ''),
        cohort_phones: _.get(campaignDetail, 'cohort_phones', []),
        eligible_stations: _.map(_.get(campaignDetail, 'eligible_stations', []), (s) => s.id || s),
      }
    : {
        name: '',
        campaign_code: '',
        amount: '',
        max_sessions: 4,
        percentage_per_session: 10,
        min_units: 5,
        start_date: '',
        end_date: '',
        cohort_group: '',
        cohort_phones: [],
        eligible_stations: [],
      };

  // CDB-1085 §9 — a campaign's logic is static once configured. Once any credit
  // has been disbursed, every field is locked so it can never diverge from the
  // credit users already hold. (The Active/Inactive pause toggle was removed
  // under CDB-1286.)
  const isDisbursed = isEdit && Number(_.get(campaignDetail, 'disbursed_user_count', 0)) > 0;
  // CDB-1288 — the start date can't be in the past; today (IST) is the earliest.
  // Shift to IST before taking the calendar day so the floor is correct even in
  // the early-morning UTC window. (Backend also rejects a past start on create.)
  const today = new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    amount: Yup.number().moreThan(0, 'Amount must be > 0').required('Amount is required'),
    max_sessions: Yup.number().integer().moreThan(0, 'N must be > 0').required('N is required'),
    percentage_per_session: Yup.number().moreThan(0).max(100).required('M% is required'),
    min_units: Yup.number().min(0).required('X is required'),
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().min(Yup.ref('start_date'), 'End date must be on or after the start date').required('End date is required'),
    // CDB-1284 — the uploaded user Excel is the cohort and is mandatory; the
    // group dropdown is an optional fallback (not required).
    cohort_phones: Yup.array().min(1, 'Please upload the cohort user list (Excel)').required('Please upload the cohort user list (Excel)'),
  });

  const onSubmit = useCallback(
    (values) => {
      const payload = {
        name: values.name,
        campaign_code: values.campaign_code,
        amount: Number(values.amount),
        max_sessions: Number(values.max_sessions),
        percentage_per_session: Number(values.percentage_per_session),
        min_units: Number(values.min_units),
        // Send the raw calendar day (YYYY-MM-DD). The backend snaps start → 00:00
        // IST and end → 23:59:59.999 IST, so a single-day campaign (14 Jul → 14
        // Jul) runs the full day and boundaries align to IST midnight. (Mirrors
        // the CDB-300 date-range handling.)
        start_date: values.start_date,
        end_date: values.end_date,
        // CDB-1284 — cohort is the uploaded phone list; group is optional.
        cohort_phones: values.cohort_phones || [],
        cohort_group: values.cohort_group || undefined,
        eligible_stations: values.eligible_stations || [],
        // No pause toggle (CDB-1286): a campaign is live once created, gated by
        // its date window + not deleted/cancelled.
        status: 'active',
        ...FIXED_POLICY,
      };
      const done = (res, code) => {
        if (_.get(res, 'status') === code) navigateTo('/preloadedCampaigns');
      };
      if (isEdit) {
        payload.id = campaignId;
        dispatch({ type: UPDATE_PRELOADED_CAMPAIGN.REQUEST, payload, cb: (r) => done(r, UPDATE_STATUS_CODE) });
      } else {
        dispatch({ type: CREATE_PRELOADED_CAMPAIGN.REQUEST, payload, cb: (r) => done(r, CREATE_STATUS_CODE) });
      }
    },
    [isEdit, campaignId]
  );

  // ---- Cohort Excel upload (CDB-1284) — the uploaded phone list IS the cohort.
  // No user group needed: phones are stored on the campaign and disburse credits
  // the matching users directly. ----
  const uploadCohort = (setFieldValue) => {
    const fileUpload = document.getElementById('cohortFile');
    const regex = /^([a-zA-Z0-9\s_\\.\-:()])+(.xls|.xlsx)$/;
    if (!regex.test(fileUpload.value.toLowerCase())) {
      toast.error('Please upload a valid Excel file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const workbook = XLSX.read(reader.result, { type: 'binary' });
      const firstSheet = workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[firstSheet]);
      // CDB-1296 — validate every row before accepting the file. A phone must be
      // 10–15 digits; we store the last 10 (country code stripped) to match how
      // users are keyed. Invalid rows are reported with their row number and the
      // upload is blocked until the file is fixed.
      const invalid = [];
      const phones = [];
      rows.forEach((r, i) => {
        const raw = String(r.phone || r.mobile || r.phone_number || r.mobile_number || '').trim();
        const rowNo = i + 2; // +1 header row, +1 for 1-based
        if (!raw) {
          invalid.push(`Row ${rowNo}: empty phone`);
          return;
        }
        const digits = raw.replace(/[^\d]/g, '');
        if (digits.length < 10 || digits.length > 15) {
          invalid.push(`Row ${rowNo}: invalid phone "${raw}"`);
          return;
        }
        phones.push(digits.slice(-10));
      });
      if (invalid.length) {
        fileUpload.value = '';
        toast.error(
          `Cohort file has ${invalid.length} invalid row(s): ${invalid.slice(0, 3).join('; ')}${invalid.length > 3 ? ' …' : ''}`
        );
        return;
      }
      const uniquePhones = _.uniq(phones);
      setFieldValue('cohort_phones', uniquePhones);
      toast.success(`${uniquePhones.length} user phone(s) loaded`);
    };
    reader.readAsBinaryString(fileUpload.files[0]);
  };

  // ---- Eligible-stations Excel upload (populates campaign.eligible_stations) ----
  const uploadStations = (setFieldValue) => {
    const fileUpload = document.getElementById('stationFile');
    const regex = /^([a-zA-Z0-9\s_\\.\-:()])+(.xls|.xlsx)$/;
    if (!regex.test(fileUpload.value.toLowerCase())) {
      toast.error('Please upload a valid Excel file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const workbook = XLSX.read(reader.result, { type: 'binary' });
      const firstSheet = workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[firstSheet]);
      // CDB-1298 — validate each station ID is a 24-char hex ObjectId before
      // accepting the file, with clear row-numbered errors, so a malformed ID
      // never reaches the API (which would return a raw "must be a valid mongo
      // id"). Existence is checked server-side on save.
      const objectIdRe = /^[a-fA-F0-9]{24}$/;
      const invalid = [];
      const ids = [];
      rows.forEach((r, i) => {
        const val = String(r.station_id || r.id || r.stationId || '').trim();
        const rowNo = i + 2;
        if (!val) {
          invalid.push(`Row ${rowNo}: empty Station ID`);
          return;
        }
        if (!objectIdRe.test(val)) {
          invalid.push(`Row ${rowNo}: invalid Station ID "${val}"`);
          return;
        }
        ids.push(val);
      });
      if (invalid.length) {
        fileUpload.value = '';
        toast.error(
          `Station file has ${invalid.length} invalid row(s): ${invalid.slice(0, 3).join('; ')}${invalid.length > 3 ? ' …' : ''}`
        );
        return;
      }
      const uniqueIds = _.uniq(ids);
      setFieldValue('eligible_stations', uniqueIds);
      toast.success(`${uniqueIds.length} station id(s) loaded`);
    };
    reader.readAsBinaryString(fileUpload.files[0]);
  };

  const downloadSample = (sample, fileName) => {
    const ws = XLSX.utils.json_to_sheet(sample);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    FileSaver.saveAs(blob, fileName);
  };

  const sectionTitle = { fontSize: 14, fontWeight: 600, opacity: 0.9, margin: '18px 0 4px' };
  const fileInputHidden = { position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' };
  const uploadBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
    padding: '10px 16px', borderRadius: 8, border: '1px dashed #4a5a6a', color: '#cdd7e0', fontSize: 13,
  };
  const linkBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#58d37d', fontSize: 13,
  };

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader
          title={isEdit ? t('header.editPreloadedCampaign', 'Edit Campaign') : t('header.addPreloadedCampaign', 'Add Campaign')}
          backTo="/preloadedCampaigns"
        />
        <div className="page-content-wrapper scrollable">
          <Card>
            <div className="p-4">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
                  <Form onSubmit={handleSubmit}>
                    {/* Header (Active/Inactive pause toggle removed under CDB-1286 —
                        a campaign is live purely by its date window). */}
                    <div
                      style={{
                        paddingBottom: 16, marginBottom: 12, borderBottom: '1px solid #2a3a48',
                      }}
                    >
                      <div style={{ fontSize: 16, fontWeight: 600 }}>Campaign details</div>
                    </div>

                    {isDisbursed && (
                      <div
                        style={{
                          padding: '10px 14px', marginBottom: 16, borderRadius: 6,
                          background: 'rgba(88,211,125,0.08)', border: '1px solid #2a3a48',
                          fontSize: 13,
                        }}
                      >
                        This campaign has been disbursed, so its settings are locked. You can
                        still add users to the cohort and update the eligible-station list via
                        the Excel uploads below — then click Disburse again to credit only the
                        newly added users (existing users are never credited twice).
                      </div>
                    )}

                    <Row>
                      <Col lg={6}>
                        <TextInput label="Name" name="name" value={values.name} onChange={handleChange} disabled={isDisbursed}
                          error={errors.name && touched.name ? errors.name : null} />
                      </Col>
                      <Col lg={6}>
                        <TextInput label="Campaign Code" name="campaign_code" value={values.campaign_code} onChange={handleChange} disabled={isDisbursed} />
                      </Col>
                      <Col lg={3}>
                        <TextInput type="number" label="Credit Amount (₹)" name="amount" value={values.amount} onChange={handleChange} disabled={isDisbursed}
                          error={errors.amount && touched.amount ? errors.amount : null} />
                      </Col>
                      <Col lg={3}>
                        <TextInput type="number" label="Max Sessions (N)" name="max_sessions" value={values.max_sessions} onChange={handleChange} disabled={isDisbursed}
                          error={errors.max_sessions && touched.max_sessions ? errors.max_sessions : null} />
                      </Col>
                      <Col lg={3}>
                        <TextInput type="number" label="% per Session (M)" name="percentage_per_session" value={values.percentage_per_session} onChange={handleChange} disabled={isDisbursed}
                          error={errors.percentage_per_session && touched.percentage_per_session ? errors.percentage_per_session : null} />
                      </Col>
                      <Col lg={3}>
                        <TextInput type="number" label="Min Units (X, kWh)" name="min_units" value={values.min_units} onChange={handleChange} disabled={isDisbursed}
                          error={errors.min_units && touched.min_units ? errors.min_units : null} />
                      </Col>
                      <Col lg={6}>
                        <TextInput type="date" label="Start Date" name="start_date" value={values.start_date} onChange={handleChange} disabled={isDisbursed}
                          min={today}
                          max={values.end_date || undefined}
                          error={errors.start_date && touched.start_date ? errors.start_date : null} />
                      </Col>
                      <Col lg={6}>
                        <TextInput type="date" label="End Date" name="end_date" value={values.end_date} onChange={handleChange} disabled={isDisbursed}
                          min={values.start_date || today}
                          error={errors.end_date && touched.end_date ? errors.end_date : null} />
                      </Col>
                    </Row>

                    {/* ---- Cohort (churned customers) ---- */}
                    <div style={sectionTitle}>Cohort (churned customers)</div>
                    <Row className="align-items-end">
                      <Col lg={6}>
                        <Select
                          label="Cohort User Group (optional)"
                          placeholder="Select cohort (optional)"
                          options={_.map(allUserGroup, (item) => ({ label: item.name, value: item.id }))}
                          value={values.cohort_group}
                          name="cohort_group"
                          onMenuScrollDown={true}
                          page={userGroupPage}
                          totalPage={userGroupTotalPage}
                          getDataOnScrollDown={getAllUserGroup}
                          onChange={(val) => setFieldValue('cohort_group', val)}
                          isDisabled={isDisbursed}
                          error={errors.cohort_group && touched.cohort_group ? errors.cohort_group : null}
                        />
                      </Col>
                      <Col lg={6}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 8 }}>
                          <label htmlFor="cohortFile" style={uploadBtn}>
                            <BsUpload /> {' '}Upload cohort (Excel)
                            {/* Excel upload stays open after disburse — lets you add
                                more users to the existing cohort, then re-disburse. */}
                            <input type="file" id="cohortFile" style={fileInputHidden}
                              onChange={() => uploadCohort(setFieldValue)} />
                          </label>
                          <span style={linkBtn} onClick={() => downloadSample([{ phone: '', country_code: '' }], 'cohort_sample.xlsx')}>
                            <FiDownload /> Download sample
                          </span>
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.6 }}>
                          Upload an Excel of user phone numbers (column: phone). This list is the campaign cohort.
                        </div>
                        {values.cohort_phones && values.cohort_phones.length > 0 && (
                          <div style={{ fontSize: 12, color: '#58d37d', marginTop: 4 }}>
                            {values.cohort_phones.length} user(s) loaded
                          </div>
                        )}
                        {errors.cohort_phones && touched.cohort_phones && (
                          <div style={{ fontSize: 12, color: '#e5533c', marginTop: 4 }}>
                            {errors.cohort_phones}
                          </div>
                        )}
                      </Col>
                    </Row>

                    {/* ---- Eligible stations ---- */}
                    <div style={sectionTitle}>Eligible stations</div>
                    <Row className="align-items-center">
                      <Col lg={6}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                          <label htmlFor="stationFile" style={uploadBtn}>
                            <BsUpload /> {' '}Upload station IDs (Excel)
                            {/* Excel upload stays open after disburse — eligible stations
                                are read live at settlement, so edits apply immediately. */}
                            <input type="file" id="stationFile" style={fileInputHidden}
                              onChange={() => uploadStations(setFieldValue)} />
                          </label>
                          <span style={linkBtn} onClick={() => downloadSample([{ station_id: '' }], 'eligible_stations_sample.xlsx')}>
                            <FiDownload /> Download sample
                          </span>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                          {(values.eligible_stations || []).length > 0
                            ? `${values.eligible_stations.length} station(s) selected — offer shows only on these`
                            : 'No stations uploaded → offer eligible on all campaign chargers'}
                        </div>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-4" style={{ gap: 12 }}>
                      <Button className="cancel_btn" onClick={() => navigateTo('/preloadedCampaigns')}>
                        {t('button.cancel', 'Cancel')}
                      </Button>
                      <Button type="submit" className="save-btn">
                        {isEdit ? t('button.update', 'Update') : t('button.save', 'Save')}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      </LayoutContainer>
    </React.Fragment>
  );
};

export default PreloadedCampaignForm;
